import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import type { SeatAvatar, SeatIdentity } from "../game/seat-identity";
import { fa } from "../i18n/fa";
import { Avatar } from "./Avatar";
import { Button } from "./Button";
import { EndCardShell } from "./EndCardShell";
import { displayName } from "./profile-ui";
import { PromoSlot } from "./PromoSlot";

/**
 * THE MATCH-END CARD — ONE TEMPLATE FOR EVERY GAME (card-98 item 12). The operator, 2026-10-27:
 * «Every game's match-end card shares one template: the winning feel of the other games' cards, plus
 * each team's player names like شلم's, with each game's own wording and results.»
 *
 *  · THE WINNING FEEL — the designer's gold-disc card تخته‌نرد / دوز / منچ already drew
 *    (docs/stitch/backgammon-ui-ux): a win is a glowing gold trophy disc, a gold title and a
 *    gold-bordered, gold-glowing card (`end-card--win`'s entrance and glow on top); a loss is a muted
 *    disc with the game's own glyph and the somber settle; a draw the muted disc with 🤝.
 *  · THE SIDES, AS شلم DRAWS A TEAM — a row a side: the game's own place mark, the side's faces
 *    overlapped, its players' names one under the other (each `dir="auto"`, cut at its end), and the
 *    side's result in the game's own figures. In a game without teams each player is a side.
 *  · EACH GAME'S OWN WORDS — the title, the reason line, a badge (تخته‌نرد's stakes), whatever the
 *    game says under its sides (شلم's last hand, پاسور's final deal) — handed in, never rewritten.
 *
 * The GEOMETRY is `EndCardShell`'s (below the table's header band, capped, the actions outside the
 * scroll). ON A SHORT SCREEN the card draws a tighter FORM — measured, not a breakpoint (شلم's P1
 * Task 9a method): «tight» where its body would scroll (a small disc beside the title, less air),
 * «tighter» where a side's row or the game's own last lines still end below the fold (a 1.25 leading,
 * the least air). Latched for the card's life; read in a layout effect, so the first painted frame is
 * already the form that fits. A game whose card is read WITHOUT A SCROLL (`fitWhole` — پاسور, card-a7
 * B8: «every screen works on his phone») steps down while its body scrolls at all, and past the
 * tighter form to a fourth, «dense» (the 28-px disc, no air between its parts): the layout probes,
 * card-98, found پاسور's card scrolling 4–22 px at 300 / 320 / 384×569 px under a 150 % zoom.
 */

export type MatchEndOutcome = "won" | "lost" | "drawn";

/** One player of a side, as the table names and draws them (the game's own resolvers). */
export interface MatchEndMember {
  readonly seat: number;
  readonly name: string;
  readonly avatar: SeatAvatar;
}

/** One side: a team, or a player where the game has no teams. */
export interface MatchEndSide {
  readonly key: string;
  /** The viewer's side — highlighted, its names bold. */
  readonly mine: boolean;
  /** The game's own place mark (🥇, ①, «برنده»…), or none. */
  readonly mark?: ReactNode;
  readonly members: readonly MatchEndMember[];
  /** The side's result, in the game's own figures (a total, دست‌ها won, a chess point…). */
  readonly result?: ReactNode;
  /** Test hooks a game's own tests find its rows by (`data-team`, `data-side`…). */
  readonly attrs?: Readonly<Record<string, string | number>>;
  /** Each player's face and name on a ROW OF THEIR OWN, the side's mark over its result on the first
   *  row (پاسور — card-a7 B8, the operator: every name WHOLE on a row of its own, never side by side). */
  readonly perMember?: boolean;
}

/**
 * A player of a table with no roster resolver of its own (شطرنج, تخته‌نرد, دوز, منچ) as its TABLE names
 * and draws them: their real name (`displayName`, the generic «کاربر» counting as none, as
 * `resolveSeatName` treats it), else the table's own label; their photo / preset, else the table's
 * preset.
 */
export function namedMember(
  seat: number,
  parts: SeatIdentity | null | undefined,
  label: string,
  preset: string,
): MatchEndMember {
  const name = parts ? displayName(parts) : "";
  return {
    seat,
    name: name && name !== fa.friends.user ? name : label,
    avatar: {
      avatarId: parts?.avatarId ?? preset,
      avatarPhotoId: parts?.avatarPhotoId ?? null,
      official: parts?.isOfficial === true,
    },
  };
}

/** The card's measured form (see the module's head). */
export type MatchEndForm = "full" | "tight" | "tighter" | "dense";
type Form = MatchEndForm;

/** The disc's size per form. */
const DISC = { full: 92, tight: 46, tighter: 36, dense: 28 } as const;

/** A trailing «🏆» in a title — the disc IS the trophy now, so it is not drawn twice. Exported so a
 *  test finds a game's title as the card draws it. */
export const titleText = (title: string): string => title.replace(/\s*🏆\s*$/u, "");
const bareTitle = (title: ReactNode): ReactNode =>
  typeof title === "string" ? titleText(title) : title;

function useFitForm(ref: { current: HTMLElement | null }, fitWhole: boolean): Form {
  const [form, setForm] = useState<Form>("full");
  useLayoutEffect(() => {
    const head = ref.current;
    const body = head?.closest<HTMLElement>("[data-end-card-body]");
    if (!head || !body || form === (fitWhole ? "dense" : "tighter")) return undefined;
    const check = (): void => {
      if (form === "full") {
        if (body.scrollHeight > body.clientHeight + 1) setForm("tight");
        return;
      }
      // read without a scroll: a step down while the body scrolls by any px (the probes' measure)
      if (fitWhole) {
        if (body.scrollHeight > body.clientHeight) setForm(form === "tight" ? "tighter" : "dense");
        return;
      }
      const fold = body.getBoundingClientRect().bottom;
      const late = [...body.querySelectorAll<HTMLElement>("[data-end-side], [data-end-fold]")].some(
        (el) => el.getBoundingClientRect().bottom > fold + 0.5,
      );
      if (late) setForm("tighter");
    };
    check();
    if (typeof window.ResizeObserver !== "function") return undefined;
    const ro = new window.ResizeObserver(check);
    ro.observe(body);
    return () => ro.disconnect();
  }, [ref, form, fitWhole]);
  return form;
}

export function MatchEndCard({
  label,
  testId,
  outcome,
  glyph,
  title,
  reason,
  badge,
  lead,
  sides,
  extra,
  promo = false,
  cardClassName,
  playAgain,
  leave,
  onAgain,
  againDisabled,
  againHint,
  againHintTighter,
  againHidden,
  onLeave,
  fitWhole = false,
}: {
  /** The dialog's accessible name — the game's «پایان بازی». */
  label: string;
  testId?: string;
  outcome: MatchEndOutcome;
  /** The disc's glyph on a loss (the game's own: 🎴, ♟️, 🎲, 🔴); a win is 🏆, a draw 🤝. */
  glyph: string;
  title: ReactNode;
  /** The line under the title — why it ended, or the game's own subtitle. */
  reason?: ReactNode;
  /** A pill under the title (تخته‌نرد's stakes). */
  badge?: ReactNode;
  /** What the game says ABOVE its sides, under the reason (پاسور's final deal: on screen where the
   *  content scrolls) — kept on screen by the tighter form too (`data-end-fold`). */
  lead?: ReactNode | ((form: MatchEndForm) => ReactNode);
  sides?: readonly MatchEndSide[];
  /** What the game says under its sides — kept on screen by the tighter form (`data-end-fold`). A
   *  function of the card's measured form, where the game says less in a tight one (شلم). */
  extra?: ReactNode | ((form: MatchEndForm) => ReactNode);
  /** The post-game promo slot — the games that had one keep it (it renders nothing today). */
  promo?: boolean;
  /** More classes on the card (تخته‌نرد's `end-card--gammon`). */
  cardClassName?: string;
  playAgain: string;
  leave: string;
  onAgain: () => void;
  againDisabled?: boolean;
  againHint?: string;
  /** The hint's short form for the tighter card (شلم's «منتظر میزبان»). */
  againHintTighter?: string;
  againHidden?: boolean;
  onLeave: () => void;
  /** The card is read WITHOUT A SCROLL wherever it can be (پاسور — card-a7 B8): it steps down while its
   *  body scrolls at all, to the «dense» form past the tighter one. */
  fitWhole?: boolean;
}): ReactNode {
  const won = outcome === "won";
  const headRef = useRef<HTMLDivElement | null>(null);
  const form = useFitForm(headRef, fitWhole);
  const tight = form !== "full";
  const dense = form === "dense";
  // the dense form is the tighter one with the least air
  const tighter = form === "tighter" || dense;
  const leading = tighter ? 1.25 : undefined;
  const disc = DISC[form];
  const hint = tighter && againHintTighter ? againHintTighter : againHint;
  const leadNode = typeof lead === "function" ? lead(form) : lead;
  const extraNode = typeof extra === "function" ? extra(form) : extra;
  const base = won
    ? "end-card end-card--win"
    : outcome === "drawn"
      ? "end-card"
      : "end-card end-card--lose";

  const discEl = (
    <div
      aria-hidden
      data-end-disc={disc}
      style={{
        width: disc,
        height: disc,
        flex: "none",
        borderRadius: "50%",
        display: "grid",
        placeItems: "center",
        background: won
          ? "radial-gradient(circle at 40% 32%, #ffe088, #af8d11 78%, #574500)"
          : "radial-gradient(circle at 40% 32%, #3a4048, #14181c)",
        border: won ? "1px solid #ffe088" : "1px solid #404943",
        boxShadow: won
          ? `0 0 ${tight ? 18 : 34}px ${tight ? 3 : 6}px rgba(255,224,136,.5), inset 0 2px 6px rgba(255,255,255,.4)`
          : "0 6px 16px rgba(0,0,0,.5)",
      }}
    >
      <span style={{ fontSize: Math.round(disc * 0.46), lineHeight: 1 }}>
        {won ? "🏆" : outcome === "drawn" ? "🤝" : glyph}
      </span>
    </div>
  );
  const titleEl = (
    <div
      style={{
        fontSize: tight ? 20 : 24,
        fontWeight: 800,
        lineHeight: leading,
        color: won ? "#e9c349" : "var(--text)",
      }}
    >
      {bareTitle(title)}
    </div>
  );

  return (
    <EndCardShell
      label={label}
      testId={testId}
      scrim="rgba(0,0,0,.6)"
      cardClassName={cardClassName ? `${base} ${cardClassName}` : base}
      cardStyle={{
        background: "var(--surface)",
        border: won ? "1px solid rgba(255,224,136,.4)" : "1px solid var(--hairline)",
        borderRadius: "var(--radius-lg)",
        maxWidth: 340,
        textAlign: "center",
        boxShadow: won
          ? "0 0 40px 4px rgba(255,224,136,.18), 0 30px 70px rgba(0,0,0,.5)"
          : "0 30px 70px rgba(0,0,0,.5)",
        gap: dense ? 4 : tight ? 8 : 12,
      }}
      actions={
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {againHidden ? null : (
            <Button variant="primary" onClick={onAgain} disabled={againDisabled === true}>
              {playAgain}
            </Button>
          )}
          {/* drawn when a hint is handed (the nine end cards' shared wiring check reads this line) */}
          {againHint ? (
            <p style={{ margin: 0, fontSize: 13, textAlign: "center", opacity: 0.75 }}>{hint}</p>
          ) : null}
          <Button variant="ghost" onClick={onLeave}>
            {leave}
          </Button>
        </div>
      }
    >
      {/* the head: the disc over the title — beside it, smaller, in the tight forms */}
      <div
        ref={headRef}
        data-end-form={form}
        style={{
          display: "flex",
          flexDirection: tight ? "row" : "column",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "center",
          gap: tight ? 10 : 12,
        }}
      >
        {discEl}
        {titleEl}
      </div>
      {badge ? (
        <div style={{ display: "flex", justifyContent: "center", marginTop: tight ? 6 : 10 }}>
          {badge}
        </div>
      ) : null}
      {reason ? (
        <div
          style={{
            fontSize: 14,
            color: "var(--hint)",
            lineHeight: leading ?? 1.6,
            marginTop: dense ? 0 : tight ? 2 : 6,
          }}
        >
          {reason}
        </div>
      ) : null}
      {leadNode ? (
        <div data-end-fold="" style={{ marginTop: dense ? 0 : tight ? 2 : 6, lineHeight: leading }}>
          {leadNode}
        </div>
      ) : null}
      {sides && sides.length > 0 ? (
        <div
          data-end-sides=""
          style={{
            background: "var(--surface-2)",
            borderRadius: "var(--radius)",
            padding: dense ? "0 4px" : tighter ? "2px 4px" : tight ? "4px" : "6px 4px",
            marginTop: dense ? 4 : tight ? 8 : 14,
          }}
        >
          {sides.map((side) => {
            const face = (m: MatchEndMember, j: number): ReactNode => (
              <span
                key={m.seat}
                data-end-face={m.seat}
                style={{ marginInlineStart: j === 0 ? 0 : -6, flex: "none", display: "flex" }}
              >
                <Avatar
                  avatarId={m.avatar.avatarId}
                  avatarPhotoId={m.avatar.avatarPhotoId}
                  official={m.avatar.official}
                  size={26}
                />
              </span>
            );
            // a name `dir="auto"`, cut at its END (an RTL block clips the BEGINNING of a Latin name)
            const name = (m: MatchEndMember): ReactNode => (
              <span
                key={m.seat}
                dir="auto"
                data-end-member={m.seat}
                style={{
                  maxWidth: "100%",
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  textAlign: "right",
                }}
              >
                {m.name}
              </span>
            );
            const names = (members: readonly MatchEndMember[]): ReactNode => (
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  fontSize: 14,
                  fontWeight: side.mine ? 700 : 500,
                  lineHeight: leading,
                }}
              >
                {members.map(name)}
              </span>
            );
            const result =
              side.result !== undefined ? (
                <span dir="ltr" data-end-result="" style={{ fontSize: 14, fontWeight: 800 }}>
                  {side.result}
                </span>
              ) : null;
            const box = {
              borderRadius: 10,
              padding: dense ? "0 12px" : tighter ? "2px 12px" : tight ? "4px 12px" : "8px 12px",
              background: side.mine
                ? "color-mix(in srgb,var(--accent) 14%,transparent)"
                : "transparent",
            };
            if (side.perMember)
              return (
                <div
                  key={side.key}
                  data-end-side={side.mine ? "mine" : "other"}
                  {...side.attrs}
                  style={{ ...box, display: "flex", flexDirection: "column", gap: 2 }}
                >
                  {side.members.map((m, j) => (
                    <div
                      key={m.seat}
                      data-end-member-row={m.seat}
                      style={{ display: "flex", alignItems: "center", gap: 10 }}
                    >
                      {face(m, 0)}
                      {names([m])}
                      {/* the side's mark OVER its result, on its first row */}
                      {j === 0 && (side.mark || result) ? (
                        <span
                          style={{
                            flex: "none",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          {side.mark || null}
                          {result}
                        </span>
                      ) : null}
                    </div>
                  ))}
                </div>
              );
            return (
              <div
                key={side.key}
                data-end-side={side.mine ? "mine" : "other"}
                {...side.attrs}
                style={{ ...box, display: "flex", alignItems: "center", gap: 10 }}
              >
                {side.mark !== undefined ? (
                  <span style={{ fontSize: 16, minWidth: 22, flex: "none" }}>{side.mark}</span>
                ) : null}
                <span style={{ display: "flex", flex: "none" }}>{side.members.map(face)}</span>
                {/* the side's players by NAME, one under the other; they give way before the result */}
                {names(side.members)}
                {result ? <span style={{ flex: "none" }}>{result}</span> : null}
              </div>
            );
          })}
        </div>
      ) : null}
      {extraNode ? (
        <div data-end-fold="" style={{ marginTop: tight ? 6 : 10, lineHeight: leading }}>
          {extraNode}
        </div>
      ) : null}
      {/* Post-game promo slot — renders nothing today (no campaign / flag off). At the END OF THE
          SCROLLING CONTENT, above the actions: a campaign must never push «خروج» off the card. */}
      {promo ? <PromoSlot slot="POST_GAME" className="mt-3" /> : null}
      <div style={{ height: dense ? 0 : tight ? 4 : 8 }} />
    </EndCardShell>
  );
}
